/**
 * StatusBadge — shared component that always pairs a text label with a color indicator.
 * Supports confidence levels (High / Needs Review / Low) and document processing statuses.
 * Uses MUI Chip as the base and pulls all colors from the MUI theme.
 * Satisfies WCAG 2.2 AA: status is never communicated by color alone.
 */

import React from 'react';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import SyncIcon from '@mui/icons-material/Sync';
import RateReviewIcon from '@mui/icons-material/RateReview';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import EditNoteIcon from '@mui/icons-material/EditNote';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import AutoAwesomeMotionIcon from '@mui/icons-material/AutoAwesomeMotion';
import SouthWestIcon from '@mui/icons-material/SouthWest';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import { styled } from '@mui/material/styles';
import { CONFIDENCE_LOW_THRESHOLD, CONFIDENCE_HIGH_THRESHOLD } from '../../config/validation';
import type { FieldValueProvenance } from '../../services/analyzerService';

// ─── Types ────────────────────────────────────────────────────────────────────

export type ConfidenceLevel = 'high' | 'needs-review' | 'low';

export type DocumentStatus =
  | 'pending'
  | 'ready'
  | 'uploading'
  | 'processing'
  | 'in_progress'
  | 'reviewing'
  | 'on_hold'
  | 'completed'
  | 'error';

type BadgeVariant = 'success' | 'warning' | 'error' | 'info' | 'neutral';

interface BadgeConfig {
  label: string;
  variant: BadgeVariant;
  icon: React.ReactElement;
  tooltip: string;
}

// ─── Styled chip ──────────────────────────────────────────────────────────────

const StyledBadgeChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'badgeVariant',
})<{ badgeVariant: BadgeVariant }>(({ theme, badgeVariant }) => {
  const palettes: Record<BadgeVariant, { bg: string; text: string; border: string }> = {
    success: {
      bg: theme.palette.success.light,
      text: theme.palette.success.contrastText,
      border: theme.palette.success.main,
    },
    warning: {
      bg: theme.palette.warning.light,
      text: theme.palette.warning.contrastText,
      border: theme.palette.warning.main,
    },
    error: {
      bg: theme.palette.error.light,
      text: theme.palette.error.contrastText,
      border: theme.palette.error.main,
    },
    info: {
      bg: theme.palette.primary.light,
      text: theme.palette.primary.contrastText,
      border: theme.palette.primary.main,
    },
    neutral: {
      bg: theme.palette.grey[200],
      text: theme.palette.text.primary,
      border: theme.palette.grey[400],
    },
  };

  const { bg, text, border } = palettes[badgeVariant];

  return {
    backgroundColor: bg,
    color: text,
    border: `1px solid ${border}`,
    fontWeight: 600,
    fontSize: '0.75rem',
    height: 24,
    '& .MuiChip-icon': {
      color: text,
    },
  };
});

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function getConfidenceLevel(confidence: number): ConfidenceLevel {
  if (confidence >= CONFIDENCE_HIGH_THRESHOLD) return 'high';
  if (confidence >= CONFIDENCE_LOW_THRESHOLD) return 'needs-review';
  return 'low';
}

function confidenceConfig(confidence: number): BadgeConfig {
  const level = getConfidenceLevel(confidence);
  switch (level) {
    case 'high':
      return {
        label: 'High',
        variant: 'success',
        icon: <CheckCircleIcon fontSize="small" />,
        tooltip: `Confidence ${confidence}% — High confidence`,
      };
    case 'needs-review':
      return {
        label: 'Needs Review',
        variant: 'warning',
        icon: <WarningIcon fontSize="small" />,
        tooltip: `Confidence ${confidence}% — Verification recommended`,
      };
    case 'low':
      return {
        label: 'Low',
        variant: 'error',
        icon: <ErrorIcon fontSize="small" />,
        tooltip: `Confidence ${confidence}% — Low confidence, review carefully`,
      };
  }
}

function documentStatusConfig(status: DocumentStatus): BadgeConfig {
  switch (status) {
    case 'completed':
      return {
        label: 'Completed',
        variant: 'success',
        icon: <CheckCircleIcon fontSize="small" />,
        tooltip: 'Document processing completed',
      };
    case 'error':
      return {
        label: 'Error',
        variant: 'error',
        icon: <ErrorIcon fontSize="small" />,
        tooltip: 'Processing failed — action required',
      };
    case 'reviewing':
      return {
        label: 'Reviewing',
        variant: 'warning',
        icon: <RateReviewIcon fontSize="small" />,
        tooltip: 'Awaiting human review',
      };
    case 'on_hold':
      return {
        label: 'On Hold',
        variant: 'neutral',
        icon: <PauseCircleOutlineIcon fontSize="small" />,
        tooltip: 'Parked with a comment for a named person — does not block export',
      };
    case 'processing':
    case 'in_progress':
      return {
        label: 'Processing',
        variant: 'info',
        icon: <SyncIcon fontSize="small" />,
        tooltip: 'Document is being processed',
      };
    case 'uploading':
      return {
        label: 'Uploading',
        variant: 'info',
        icon: <SyncIcon fontSize="small" />,
        tooltip: 'Document is uploading',
      };
    case 'ready':
      return {
        label: 'Ready to Validate',
        variant: 'info',
        icon: <HourglassEmptyIcon fontSize="small" />,
        tooltip: 'Processed — awaiting analyst review',
      };
    case 'pending':
    default:
      return {
        label: 'Pending',
        variant: 'neutral',
        icon: <HourglassEmptyIcon fontSize="small" />,
        tooltip: 'Awaiting processing',
      };
  }
}

/**
 * Where a field's value came from (Lens S5). `FieldValueProvenance` in `analyzerService` is the
 * contract; this renders it. Kept in this component rather than as a one-off Chip in the Lens
 * panel because provenance is exactly what StatusBadge exists for — a status that must read as
 * text plus a shape, never as a color alone (a clerk deciding whether to trust a value cannot be
 * asked to distinguish two chip hues).
 *
 * `'operator'` deliberately carries the strongest treatment: a value a person typed over the
 * extraction is the one a reader most needs to notice.
 */
function provenanceConfig(provenance: FieldValueProvenance): BadgeConfig {
  switch (provenance) {
    case 'operator':
      return {
        label: 'Corrected',
        variant: 'warning',
        icon: <EditNoteIcon fontSize="small" />,
        tooltip: 'Corrected by a reviewer during validation — replaces the extracted value',
      };
    case 'derived':
      return {
        label: 'Derived',
        variant: 'info',
        icon: <CalculateOutlinedIcon fontSize="small" />,
        tooltip: 'Computed from other extracted fields, not read from the page',
      };
    case 'enriched':
      return {
        label: 'Enriched',
        variant: 'info',
        icon: <AutoAwesomeMotionIcon fontSize="small" />,
        tooltip: 'Appended by an enricher — not present in the source document',
      };
    case 'inbound':
      return {
        label: 'From system of record',
        variant: 'neutral',
        icon: <SouthWestIcon fontSize="small" />,
        tooltip: 'Returned by the system of record after delivery',
      };
    case 'extracted':
    default:
      return {
        label: 'Extracted',
        variant: 'neutral',
        icon: <DocumentScannerIcon fontSize="small" />,
        tooltip: 'Read directly from the source document by the analyzer',
      };
  }
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface ConfidenceBadgeProps {
  type: 'confidence';
  /** Numeric confidence score, 0–100 */
  confidence: number;
  size?: 'small' | 'medium';
  className?: string;
}

interface DocumentStatusBadgeProps {
  type: 'document-status';
  status: DocumentStatus;
  size?: 'small' | 'medium';
  className?: string;
}

interface ProvenanceBadgeProps {
  type: 'provenance';
  provenance: FieldValueProvenance;
  size?: 'small' | 'medium';
  className?: string;
}

export type StatusBadgeProps = ConfidenceBadgeProps | DocumentStatusBadgeProps | ProvenanceBadgeProps;

// ─── Component ────────────────────────────────────────────────────────────────

const StatusBadge: React.FC<StatusBadgeProps> = (props) => {
  const config: BadgeConfig =
    props.type === 'confidence'
      ? confidenceConfig(props.confidence)
      : props.type === 'provenance'
        ? provenanceConfig(props.provenance)
        : documentStatusConfig(props.status);

  const size = props.size ?? 'small';

  return (
    <Tooltip title={config.tooltip} placement="top" arrow>
      <StyledBadgeChip
        icon={config.icon}
        label={config.label}
        badgeVariant={config.variant}
        size={size}
        variant="outlined"
        className={props.className}
      />
    </Tooltip>
  );
};

export default StatusBadge;
